import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { GameStatus } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineS, LabelS } from '@sorare/core/src/atoms/typography';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GameSelection_GameInterface } from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ center: true })``;
const GameInfosWrapper = styled.div`
  display: grid;
  align-items: center;
  justify-items: center;
  gap: var(--intermediate-unit);
  width: 100%;
  grid-template-columns: minmax(0, 1fr) max-content minmax(0, 1fr);
  overflow: hidden;
  @media ${tabletAndAbove} {
    gap: var(--triple-unit);
  }
`;

const Team = styled(Vertical).attrs({ center: true })`
  flex: 1;
  justify-content: stretch;
`;
const TeamName = styled(LabelS).attrs({ as: 'span' })`
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: left;
`;

const Infos = styled(Vertical).attrs({ gap: 0, center: true })`
  z-index: 1;
  justify-content: stretch;
`;

const TeamLogo = styled.img`
  width: var(--quadruple-unit);
`;

const SecondLine = styled(Horizontal).attrs({ gap: 0.5 })`
  color: var(--c-neutral-500);
  font: var(--t-label-s);
`;

type GameInfosProps = {
  game: GameSelection_GameInterface;
  liveGameTimeStatus?: ReactNode;
};
const GameInfos = ({ game, liveGameTimeStatus }: GameInfosProps) => {
  const { startDate, status, awayScore, homeScore } = game;
  const { formatTime } = useIntl();
  if (status === GameStatus.scheduled) {
    const [time, dayPeriod] = formatTime(startDate, {
      hour: 'numeric',
      minute: 'numeric',
    }).split(' ');
    return (
      <Vertical center gap={0}>
        <HeadlineS as="span">{time}</HeadlineS>
        {dayPeriod && <SecondLine>{dayPeriod}</SecondLine>}
      </Vertical>
    );
  }

  return (
    <>
      <BodyM as="span" bold>
        {awayScore} - {homeScore}
      </BodyM>
      <>
        {status === GameStatus.playing && (
          <SecondLine>{liveGameTimeStatus || <LiveDot animate />}</SecondLine>
        )}
      </>
    </>
  );
};

type Props = {
  game: GameSelection_GameInterface;
  darkThemeSvg?: boolean;
  liveGameTimeStatus?: ReactNode;
  extra?: ReactNode;
};

export const GameSelection = ({
  game,
  liveGameTimeStatus,
  extra = null,
  darkThemeSvg = false,
}: Props) => {
  const { homeTeam, awayTeam } = game;

  return (
    <Wrapper>
      <GameInfosWrapper>
        <Team>
          <TeamLogo
            src={darkThemeSvg ? awayTeam?.darkThemeSvgUrl : awayTeam?.svgUrl}
            alt=""
          />
          <TeamName>{awayTeam?.name}</TeamName>
        </Team>
        <Infos>
          <GameInfos game={game} liveGameTimeStatus={liveGameTimeStatus} />
        </Infos>
        <Team>
          <TeamLogo
            src={darkThemeSvg ? homeTeam?.darkThemeSvgUrl : homeTeam?.svgUrl}
            alt=""
          />
          <TeamName>{homeTeam?.name}</TeamName>
        </Team>
      </GameInfosWrapper>
      {extra}
    </Wrapper>
  );
};

GameSelection.fragments = {
  GameInterface: gql`
    fragment GameSelection_GameInterface on GameInterface {
      id
      status
      startDate
      homeScore
      awayScore
      homeTeam {
        slug
        name
        svgUrl
        darkThemeSvgUrl
      }
      awayTeam {
        slug
        name
        svgUrl
        darkThemeSvgUrl
      }
    }
  ` as TypedDocumentNode<GameSelection_GameInterface>,
};
